import { Box, InputAdornment } from "@mui/material";
import { useContext, useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import authorityUserType from "../../assets/data/TkAuthorityProperty";
import { labelids } from "../../assets/data/TkExplanationProperty";
import TkButton from "../../components/atoms/TkButton";
import TkInfoManual from "../../components/atoms/TkInfoManual";
import TkLabelText from "../../components/atoms/TkLabelText";
import TkTable from "../../components/atoms/TkTable";
import TkBackButton from "../../components/molecules/TkBackButton";
import TkContractorAndNumberLine from "../../components/molecules/TkContractorAndNumberLine";
import { BusinessContext } from "../../context/businessContext";
import { GlobalContext } from "../../context/globalContext";
import { TkRouteContext } from "../../context/tkRouteContext";
import {
  CheckedList,
  ChildcareLeaveList,
  IAPI_STA006_Request,
} from "../../type/apiType/IAPI_STA006";
import {
  getYearMonth,
  getYearMonthAfterTwelfth,
  getYearMonthElevenDaysBeforeMonthEnd,
  getYearMonthHasMoreThanElevenDays,
} from "../../utils/TkChildcareLeaveDate";
import "./_style.scss";
import { httpAPI_STA006 } from "./api/request";
import { headList, headListWorkingDayNum } from "./parts";
import { TkGetCode } from "../../utils/TkGetCode";
import { PapaMamaPlus } from "../../assets/data/Constants";
import TkPropertyMessageGet from "../../utils/TkPropertyMessageGet";
import TkMessageDialog from "../../components/molecules/TkMessageDialog";
import _ from "lodash";

/**
 * SCRC060102 育児休業情報確認
 * 育児休業情報登録画面で入力した育児休業情報を確認する。
 * @return SCRC060102
 */
const SCRC060102 = () => {
  const navigate = useNavigate();
  const { state, setState } = useContext(GlobalContext);
  const { state: businessState } = useContext(BusinessContext);
  const { state: stateRouter, setState: setStateRouter } =
    useContext(TkRouteContext);
  const [bodyList, setBodyList] = useState<any>();
  const [bodyListWorkingDayNum, setBodyListWorkingDayNum] = useState<any>();
  const [workingDayNumList, setWorkingDayNumList] = useState<any[]>([]);
  const [errorList, setErrorList] = useState<number[]>([]);
  const [stateDialog, setStateDialog] = useState(false);
  const [openInfo, setOpenInfo] = useState(false);

  const debouncedSetState = useRef(
    _.debounce((newStateUpdater: any) => {
      setWorkingDayNumList(newStateUpdater);
    }, 300)
  ).current;

  useEffect(() => {
    return () => {
      debouncedSetState.cancel();
    };
  }, [debouncedSetState]);

  // 認証情報.ユーザタイプ
  const userType = state?.authInfo?.userType;
  // ユーザタイプが機構
  const userTypeKiko = userType === authorityUserType.userTypeNumber.KIKO;
  // 共済契約者番号
  const contractorNo = userTypeKiko
    ? businessState?.common?.contractorNo!
    : state?.authInfo?.contractorNo!;
  // 職員氏名（画面間パラメータ）
  const staffNo = businessState.childcareLeave?.staffNo;

  // 育休取得情報リスト
  const childcareLeaveInfoList =
    businessState.childcareLeave?.childcareLeaveRegistrationInfo
      ?.childcareLeaveInfoList;
  // 育休出勤日数リスト
  const childcareLeaveWorkingDayNumList =
    businessState.childcareLeave?.childcareLeaveRegistrationInfo
      ?.childcareActiveListList;
  // チェック済みリスト
  const [checkedList, setCheckedList] = useState<CheckedList[]>([]);

  const handleChange = (key: number, index: number) => (value: any) => {
    let result = value.target.value;
    setErrorList((prevState) => {
      if (isInvalidWorkingDayNum(result)) {
        return [...prevState, index];
      } else {
        return prevState.filter((item) => item !== index);
      }
    });

    debouncedSetState(
      workingDayNumList.map((item) => {
        if (item.yearMonth === key) {
          return { ...item, workingDayNum: parseInt(result, 10) };
        }
        return item;
      })
    );
  };

  // 入力値のチェック
  const isInvalidWorkingDayNum = (value: any) => {
    const num = Number(value);
    return value === "" || Number.isNaN(num) || num < 0 || num > 31;
  };

  const validateWorkingDayNumList = (list: any[]) => {
    const invalidIndexes: number[] = [];

    list.forEach((item, index) => {
      if (isInvalidWorkingDayNum(item.workingDayNum)) {
        invalidIndexes.push(index);
      }
    });

    setErrorList(invalidIndexes);
    return invalidIndexes.length === 0;
  };

  // 登録ボタンを押下した時、入力値のチェックを行う
  const handleRegister = () => {
    const isValid = validateWorkingDayNumList(workingDayNumList);
    if (!isValid) {
      setStateDialog(false);
      return;
    }
    setStateDialog(true);
  };

  const handleInit = async () => {
    const bodyListChildcareLeave = childcareLeaveInfoList?.map((item) => [
      {
        children: item.birthday,
      },
      {
        children: item.startDate,
      },
      {
        children: item.endDate,
      },
      {
        children: TkGetCode(PapaMamaPlus, item.papaMamaPlus),
      },
      {
        children: item.halfyearExtensionReason,
      },
      {
        children: item.papaChildcareLeaveFlg === "1" ? "有" : "無",
      },
    ]);
    setBodyList(bodyListChildcareLeave);

    //「出勤日数」の対象年月
    const targetYearMonth: Array<number> = [];
    childcareLeaveInfoList?.forEach((item) => {
      // 産後パパ育休が「有」の場合
      if (item.papaChildcareLeaveFlg === "1") {
        targetYearMonth.push(getYearMonth(item?.startDate));
        targetYearMonth.push(getYearMonth(item?.endDate));
      } else {
        // 産後パパ育休が「無」の場合
        targetYearMonth.push(getYearMonthAfterTwelfth(item?.startDate));
        targetYearMonth.push(
          getYearMonthElevenDaysBeforeMonthEnd(item?.endDate)
        );
        targetYearMonth.push(
          getYearMonthHasMoreThanElevenDays(item?.startDate, item?.endDate)
        );
      }
    });
    // 生成した不要データを削除する
    const filteredTargetYearMonth = targetYearMonth.filter(
      (yearMonth) => yearMonth !== -1
    );
    // 重複を除去する
    const uniqueTargetYearMonth = Array.from(new Set(filteredTargetYearMonth));
    // 未来月を除外する
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;
    const validTargetYearMonth = uniqueTargetYearMonth.filter((yearMonth) => {
      const year = Math.floor(yearMonth / 100);
      const month = yearMonth % 100;
      return (
        year < currentYear || (year === currentYear && month <= currentMonth)
      );
    });

    const sortedTargetYearMonth = validTargetYearMonth.sort((a, b) => a - b);
    const getWorkingDayNum = (item: any) => {
      const result = childcareLeaveWorkingDayNumList?.find(
        (i) => i.yearMonth === item
      );
      if (!!result) {
        return result;
      } else {
        return {
          yearMonth: item,
          workingDayNum: "",
        };
      }
    };
    const resultWorkingDayNumList = sortedTargetYearMonth.map((item) => ({
      yearMonth: getWorkingDayNum(item)?.yearMonth,
      workingDayNum: getWorkingDayNum(item)?.workingDayNum,
    }));
    setWorkingDayNumList(resultWorkingDayNumList);

    setState({
      ...state,
      errorMsgId: "",
    });
  };

  //初期表示
  useEffect(() => {
    handleInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const bodyListWorkingDayNum = workingDayNumList.map((item, index) => [
      {
        children: `${(item.yearMonth + "").slice(0, 4)}/${(
          item.yearMonth + ""
        ).slice(4)}`,
        align: "center",
      },
      {
        children: (
          <TkLabelText
            fullWidth
            onlyNumber
            inputProps={{
              maxLength: 2,
              style: { textAlign: "end" },
            }}
            InputProps={{
              endAdornment: <InputAdornment position="end">日</InputAdornment>,
            }}
            wantToSetValue={item.workingDayNum >= 0 ? item.workingDayNum : ""}
            onChange={handleChange(item.yearMonth, index)}
            error={errorList.some((item) => item === index)}
            helperText={
              errorList.some((item) => item === index)
                ? TkPropertyMessageGet("TKMSG_SCR0048E", "31")
                : ""
            }
          ></TkLabelText>
        ),
      },
    ]);
    setBodyListWorkingDayNum(bodyListWorkingDayNum);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workingDayNumList, errorList]);

  const makeHttpObj = (): IAPI_STA006_Request => {
    const childcareLeaveList: ChildcareLeaveList[] =
      childcareLeaveInfoList?.map((item, index) => ({
        contractorNo: contractorNo,
        staffNo: staffNo,
        updateDateTime:
          businessState.childcareLeave?.childcareLeaveRegistrationInfo
            ?.updateDateTime!,
        childcareLeaveSerialNo: index + 1,
        birthday: item.birthday!,
        startDate: item.startDate!,
        endDate: item.endDate!,
        papaMamaFlg: item.papaMamaPlus!,
        halfyearExtensionReason: item.halfyearExtensionReason,
        papaChildcareLeaveFlg: item.papaChildcareLeaveFlg!,
        businessType: businessState.common?.businessType,
      }))!;

    const reqObj: IAPI_STA006_Request = {
      childcareLeaveList: childcareLeaveList!,
      workingDayNumList: workingDayNumList,
      checkedList: checkedList,
    };

    return reqObj;
  };

  const onCheckAndSubmit = async () => {
    if (errorList.length > 0) {
      return;
    }
    //日々更新法人職員育休更新API実行
    try {
      const res = (await httpAPI_STA006({
        ...makeHttpObj(),
      })) as { [key: string]: any };

      setCheckedList(res.checkedList || []);

      if (res.resultCd === "I0000") {
        setState({
          ...state,
          errorMsgId: "",
        });
        handlePageTransition();
      } else if (res.resultCd === "TKMSG_SVR0001I") {
        setState({
          ...state,
          errorMsgId: "",
        });
        setStateDialog(false);
        setOpenInfo(true);
      } else {
        setStateDialog(false);
        setState({
          ...state,
          errorMsgId: res.resultCd,
        });
      }
    } catch (error) {
      navigate("/error");
    }
  };

  const handlePageTransition = () => {
    // API呼び出しの結果が正常終了した場合、二つ前の遷移元画面に遷移する。
    let routerList = stateRouter.routerList || [];
    if (routerList.length >= 3) {
      setStateRouter({
        ...state,
        routerList: [...routerList.slice(0, routerList.length - 3)],
      });
      setCheckedList([]);
      navigate(`${routerList[routerList.length - 3].href}`, {
        state: { backButtonFlag: true },
      });
    } else {
      // エラー内容を表示する。
      setStateRouter({
        ...state,
        routerList: [...routerList],
      });
      navigate("/error");
    }
  };

  return (
    <Box className="SCRC060102" data-testid="SCRC060102PageForTest">
      <TkInfoManual rule={"\\n"}>{labelids.labelid.LBLC060102_01}</TkInfoManual>

      {userTypeKiko && (
        <TkContractorAndNumberLine
          name="契約者（法人）名"
          no={`${businessState.common?.contractorNo ?? ""}:${
            businessState.common?.contractorName ?? ""
          }`}
        />
      )}

      <TkTable headList={headList} bodyList={bodyList} />

      {bodyListWorkingDayNum && bodyListWorkingDayNum.length ? (
        <>
          <TkInfoManual rule={"\\n"}>
            {labelids.labelid.LBLC060102_02}
          </TkInfoManual>

          <TkTable
            headList={headListWorkingDayNum}
            bodyList={bodyListWorkingDayNum}
          />
        </>
      ) : null}

      <Box className="buttonGroup">
        <TkBackButton useBackParam />
        <TkButton onClick={handleRegister}>登録</TkButton>
      </Box>

      <TkMessageDialog
        open={stateDialog}
        onClose={() => setStateDialog(false)}
        onCancel={() => setStateDialog(false)}
        onOky={onCheckAndSubmit}
        title="確認（情報を登録します）"
        contextText="　入力した内容で登録を行いますが、問題ないでしょうか。"
        okText="はい"
        cancelText="いいえ"
      />

      <TkMessageDialog
        open={openInfo}
        onOky={handlePageTransition}
        contextText={TkPropertyMessageGet("TKMSG_SVR0001I")}
        okText="確認"
        cancelText=""
      />
    </Box>
  );
};

export default SCRC060102;
